import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, DollarSign, Calendar, FileText, UserPlus, Eye, Building, CreditCard, Printer, CheckCircle, FileDown, FileSpreadsheet } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { ChevronsUpDown, Check } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatCurrency } from '@/utils/dateFormat';
import { useCurrency } from '@/contexts/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_egg-inventory-pro/artifacts/vtgmvoj5_Logo%201.jpg";
const COMPANY_NAME = "Wilyrash Farm";
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const PayrollManagement = () => {
  const { currencySymbol } = useCurrency();
  const [staffList, setStaffList] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [recentViews, setRecentViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payrollOpen, setPayrollOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [viewPayslipOpen, setViewPayslipOpen] = useState(false);
  const [viewingPayslip, setViewingPayslip] = useState(null);
  const [viewingEmployeeProfile, setViewingEmployeeProfile] = useState(null);
  const [otherPeriodOffDaysSpent, setOtherPeriodOffDaysSpent] = useState(0);
  const payslipPrintRef = useRef(null);
  
  // Payroll History state
  const [historyTab, setHistoryTab] = useState('payroll'); // 'payroll', 'employees', 'history'
  const [selectedHistoryEmployee, setSelectedHistoryEmployee] = useState(null);
  const [employeePayrollHistory, setEmployeePayrollHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Re-link payslips (fix payslips linked to an old/wrong account id)
  const [relinkOpen, setRelinkOpen] = useState(false);
  const [relinkName, setRelinkName] = useState('');
  const [relinkTargetId, setRelinkTargetId] = useState('');
  const [relinkMatches, setRelinkMatches] = useState(null);
  const [relinkBusy, setRelinkBusy] = useState(false);
  const [relinkTargetOpen, setRelinkTargetOpen] = useState(false);

  const handleRelinkPreview = async () => {
    if (!relinkName.trim()) { toast.error('Enter the staff name to search'); return; }
    setRelinkBusy(true);
    try {
      const res = await axios.get(`${API}/payroll/relink-preview?name=${encodeURIComponent(relinkName.trim())}`, getAuthHeader());
      setRelinkMatches(res.data.matches || []);
      if ((res.data.count || 0) === 0) toast.info('No payslips found for that name');
    } catch (e) {
      toast.error('Failed to search payslips');
    } finally {
      setRelinkBusy(false);
    }
  };

  const handleRelinkCommit = async () => {
    if (!relinkTargetId) { toast.error('Select the correct employee to link to'); return; }
    if (!relinkMatches || relinkMatches.length === 0) { toast.error('Search and review the payslips first'); return; }
    setRelinkBusy(true);
    try {
      const ids = relinkMatches.map((m) => m.id);
      const res = await axios.post(
        `${API}/payroll/relink?target_user_id=${relinkTargetId}`,
        { payroll_ids: ids }, getAuthHeader()
      );
      toast.success(`Re-linked ${res.data.updated} payslip(s) to ${res.data.target_name}`);
      setRelinkOpen(false);
      setRelinkMatches(null);
      setRelinkName('');
      setRelinkTargetId('');
      fetchPayrolls();
    } catch (e) {
      toast.error('Failed to re-link payslips');
    } finally {
      setRelinkBusy(false);
    }
  };
  
  // Get current user role from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user.role || '';
  
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  // Print payslip function
  const handlePrintPayslip = () => {
    const printContent = payslipPrintRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Pay Slip - ${viewingPayslip?.staff_name || 'Employee'}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20px; color: #333; }
            .payslip-container { max-width: 800px; margin: 0 auto; }
            .company-header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #10b981; }
            .company-logo { width: 80px; height: 80px; border-radius: 50%; object-fit: cover; margin-bottom: 10px; }
            .company-name { font-size: 24px; font-weight: bold; color: #10b981; }
            .company-tagline { font-size: 12px; color: #666; }
            .payslip-title { font-size: 18px; font-weight: bold; margin-top: 15px; color: #333; }
            .employee-info { background: #f0fdf4; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .employee-info h3 { margin-bottom: 10px; color: #10b981; }
            .info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
            .info-item label { font-size: 11px; color: #666; display: block; }
            .info-item span { font-weight: 600; }
            .section { margin-bottom: 20px; padding: 15px; border: 1px solid #e2e8f0; border-radius: 8px; }
            .section h4 { margin-bottom: 10px; padding-bottom: 5px; border-bottom: 1px solid #e2e8f0; }
            .section.additions { background: #f0fdf4; border-color: #10b981; }
            .section.additions h4 { color: #10b981; }
            .section.deductions { background: #fef2f2; border-color: #ef4444; }
            .section.deductions h4 { color: #ef4444; }
            .row { display: flex; justify-content: space-between; padding: 5px 0; }
            .row.total { border-top: 1px solid #ccc; margin-top: 10px; padding-top: 10px; font-weight: bold; }
            .amount.positive { color: #10b981; }
            .amount.negative { color: #ef4444; }
            .net-pay { background: linear-gradient(to right, #f0fdf4, white); padding: 20px; border-radius: 8px; text-align: right; margin: 20px 0; }
            .net-pay-label { font-size: 14px; color: #666; }
            .net-pay-amount { font-size: 28px; font-weight: bold; color: #10b981; }
            .ytd-section { background: #eff6ff; padding: 15px; border-radius: 8px; }
            .ytd-section h4 { color: #3b82f6; margin-bottom: 10px; }
            .ytd-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
            .ytd-item label { font-size: 11px; color: #666; display: block; }
            .ytd-item span { font-size: 18px; font-weight: bold; color: #3b82f6; }
            .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #666; }
            @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <div class="payslip-container">
            ${printContent.innerHTML}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };
  
  const [filterMonth, setFilterMonth] = useState(currentMonth);
  const [filterYear, setFilterYear] = useState(currentYear);
  const [filterPeriod, setFilterPeriod] = useState('all'); // 'all', '1', or '2'
  
  const [payrollForm, setPayrollForm] = useState({
    user_id: '',
    month: currentMonth,
    period: 1,
    year: currentYear,
    days_worked: '',
    entitled_day_offs: '0',
    off_days_spent: '0',
    absent_days: '0',
    // Additions
    data_allowance: '0',
    medical_allowance: '0',
    offsite_duty_allowance: '0',
    // Multiple bonus fields
    bonus_1_description: '',
    bonus_1_amount: '0',
    bonus_2_description: '',
    bonus_2_amount: '0',
    bonus_3_description: '',
    bonus_3_amount: '0',
    // Deductions
    loan_repayment: '0',
    damages: '0',
    other_deduction_amount: '0',
    other_deduction_description: '',
    // Staff Loan (principal + date)
    loan_amount: '0',
    loan_disbursed_date: '',
    // Medical Reimbursement (for record only)
    med_reimburse_amount: '0',
    med_reimburse_date: '',
    med_reimburse_reason: '',
    // Year-to-date totals (Admin editable)
    ytd_gross_pay: '',
    ytd_net_pay: '',
    // Legacy fields
    other_deductions: '0',
    bonuses: '0',
    bonus: '0',
    payment_date: '',
    payment_status: 'pending',
    notes: ''
  });
  
  const [profileForm, setProfileForm] = useState({
    user_id: '',
    employee_name: '',
    employee_id: '',
    date_of_hire: '',
    bank_name: '',
    bank_account_name: '',
    bank_account_number: '',
    base_salary: ''
  });

  useEffect(() => {
    fetchStaffList();
    fetchPayrolls();
    fetchRecentViews();
  }, [filterMonth, filterYear, filterPeriod]);

  // Fetch payroll history when employee is selected
  const fetchEmployeePayrollHistory = async (userId) => {
    if (!userId) {
      setEmployeePayrollHistory([]);
      return;
    }
    
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/payroll/staff/${userId}`, getAuthHeader());
      setEmployeePayrollHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch payroll history:', error);
      toast.error('Failed to load payroll history');
      setEmployeePayrollHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSelectHistoryEmployee = (userId) => {
    setSelectedHistoryEmployee(userId);
    fetchEmployeePayrollHistory(userId);
  };

  // Calculate history summary for an employee
  const getHistorySummary = () => {
    if (employeePayrollHistory.length === 0) return null;
    
    const totalGross = employeePayrollHistory.reduce((sum, p) => sum + (p.gross_pay || 0), 0);
    const totalNet = employeePayrollHistory.reduce((sum, p) => sum + (p.net_pay || 0), 0);
    const totalDeductions = employeePayrollHistory.reduce((sum, p) => sum + (p.total_deductions || 0), 0);
    
    return {
      payslipCount: employeePayrollHistory.length,
      totalGross,
      totalNet,
      totalDeductions,
      avgGross: totalGross / employeePayrollHistory.length,
      avgNet: totalNet / employeePayrollHistory.length
    };
  };

  const fetchRecentViews = async () => {
    try {
      const response = await axios.get(`${API}/payroll/views/recent`, getAuthHeader());
      setRecentViews(response.data);
    } catch (error) {
      console.log('Could not fetch recent views');
    }
  };

  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${API}/staff-for-payroll`, getAuthHeader());
      setStaffList(response.data);
    } catch (error) {
      toast.error('Failed to fetch staff list');
    }
  };

  const fetchPayrolls = async () => {
    try {
      setLoading(true);
      let url = `${API}/payroll?month=${filterMonth}&year=${filterYear}`;
      if (filterPeriod !== 'all') {
        url += `&period=${filterPeriod}`;
      }
      const response = await axios.get(url, getAuthHeader());
      setPayrolls(response.data);
    } catch (error) {
      toast.error('Failed to fetch payrolls');
    } finally {
      setLoading(false);
    }
  };

  // Fetch other period's off-days spent when relevant form fields change
  const fetchOtherPeriodOffDays = async (userId, month, year, period) => {
    if (!userId || !month || !year || !period) {
      setOtherPeriodOffDaysSpent(0);
      return;
    }
    try {
      const response = await axios.get(
        `${API}/payroll/other-period-offdays?user_id=${userId}&month=${month}&year=${year}&current_period=${period}`,
        getAuthHeader()
      );
      setOtherPeriodOffDaysSpent(response.data.off_days_spent || 0);
    } catch (error) {
      console.error('Failed to fetch other period off-days:', error);
      setOtherPeriodOffDaysSpent(0);
    }
  };

  const handleStaffSelect = (userId) => {
    const staff = staffList.find(s => s.user.id === userId);
    setSelectedStaff(staff);
    setPayrollForm({ ...payrollForm, user_id: userId });
    // Fetch other period's off-days
    fetchOtherPeriodOffDays(userId, payrollForm.month, payrollForm.year, payrollForm.period);
  };

  const handlePayrollSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...payrollForm,
        month: parseInt(payrollForm.month),
        year: parseInt(payrollForm.year),
        days_worked: parseInt(payrollForm.days_worked) || 0,
        entitled_day_offs: parseInt(payrollForm.entitled_day_offs) || 0,
        off_days_spent: parseInt(payrollForm.off_days_spent) || 0,
        absent_days: parseInt(payrollForm.absent_days) || 0,
        // Additions
        data_allowance: parseFloat(payrollForm.data_allowance) || 0,
        medical_allowance: parseFloat(payrollForm.medical_allowance) || 0,
        offsite_duty_allowance: parseFloat(payrollForm.offsite_duty_allowance) || 0,
        // Multiple bonus fields
        bonus_1_description: payrollForm.bonus_1_description || null,
        bonus_1_amount: parseFloat(payrollForm.bonus_1_amount) || 0,
        bonus_2_description: payrollForm.bonus_2_description || null,
        bonus_2_amount: parseFloat(payrollForm.bonus_2_amount) || 0,
        bonus_3_description: payrollForm.bonus_3_description || null,
        bonus_3_amount: parseFloat(payrollForm.bonus_3_amount) || 0,
        // Deductions
        loan_repayment: parseFloat(payrollForm.loan_repayment) || 0,
        damages: parseFloat(payrollForm.damages) || 0,
        other_deduction_amount: parseFloat(payrollForm.other_deduction_amount) || 0,
        other_deduction_description: payrollForm.other_deduction_description || null,
        // Staff Loan
        loan_amount: parseFloat(payrollForm.loan_amount) || 0,
        loan_disbursed_date: payrollForm.loan_disbursed_date || null,
        // Medical Reimbursement (for record only)
        med_reimburse_amount: parseFloat(payrollForm.med_reimburse_amount) || 0,
        med_reimburse_date: payrollForm.med_reimburse_date || null,
        med_reimburse_reason: payrollForm.med_reimburse_reason || null,
        // Year-to-date overrides (only include if Admin provided values)
        ytd_gross_pay: payrollForm.ytd_gross_pay ? parseFloat(payrollForm.ytd_gross_pay) : null,
        ytd_net_pay: payrollForm.ytd_net_pay ? parseFloat(payrollForm.ytd_net_pay) : null,
        // Legacy fields
        other_deductions: parseFloat(payrollForm.other_deductions) || 0,
        bonuses: parseFloat(payrollForm.bonuses) || 0,
        bonus: parseFloat(payrollForm.bonus) || 0
      };
      
      if (editingPayroll) {
        await axios.put(`${API}/payroll/${editingPayroll.id}`, data, getAuthHeader());
        toast.success('Payroll updated successfully');
      } else {
        await axios.post(`${API}/payroll`, data, getAuthHeader());
        toast.success('Payroll created successfully');
      }
      
      handlePayrollClose();
      fetchPayrolls();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payroll');
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...profileForm,
        base_salary: parseFloat(profileForm.base_salary) || 0
      };
      
      // Check if profile exists
      const staff = staffList.find(s => s.user.id === profileForm.user_id);
      if (staff?.profile) {
        await axios.put(`${API}/employee-profiles/${profileForm.user_id}`, data, getAuthHeader());
        toast.success('Employee profile updated successfully');
      } else {
        await axios.post(`${API}/employee-profiles`, data, getAuthHeader());
        toast.success('Employee profile created successfully');
      }
      
      handleProfileClose();
      fetchStaffList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    }
  };

  const handleEditPayroll = (payroll) => {
    setEditingPayroll(payroll);
    setPayrollForm({
      user_id: payroll.user_id,
      month: payroll.month,
      period: payroll.period || 1,
      year: payroll.year,
      days_worked: payroll.days_worked.toString(),
      entitled_day_offs: payroll.entitled_day_offs.toString(),
      off_days_spent: (payroll.off_days_spent || 0).toString(),
      absent_days: payroll.absent_days.toString(),
      // Additions
      data_allowance: (payroll.data_allowance || 0).toString(),
      medical_allowance: (payroll.medical_allowance || 0).toString(),
      offsite_duty_allowance: (payroll.offsite_duty_allowance || 0).toString(),
      // Multiple bonus fields
      bonus_1_description: payroll.bonus_1_description || '',
      bonus_1_amount: (payroll.bonus_1_amount || 0).toString(),
      bonus_2_description: payroll.bonus_2_description || '',
      bonus_2_amount: (payroll.bonus_2_amount || 0).toString(),
      bonus_3_description: payroll.bonus_3_description || '',
      bonus_3_amount: (payroll.bonus_3_amount || 0).toString(),
      // Deductions
      loan_repayment: (payroll.loan_repayment || 0).toString(),
      damages: (payroll.damages || 0).toString(),
      other_deduction_amount: (payroll.other_deduction_amount || 0).toString(),
      other_deduction_description: payroll.other_deduction_description || '',
      // Staff Loan
      loan_amount: (payroll.loan_amount || 0).toString(),
      loan_disbursed_date: payroll.loan_disbursed_date || '',
      // Medical Reimbursement
      med_reimburse_amount: (payroll.med_reimburse_amount || 0).toString(),
      med_reimburse_date: payroll.med_reimburse_date || '',
      med_reimburse_reason: payroll.med_reimburse_reason || '',
      // Year-to-date totals
      ytd_gross_pay: (payroll.ytd_gross_pay || 0).toString(),
      ytd_net_pay: (payroll.ytd_net_pay || 0).toString(),
      // Legacy fields
      other_deductions: (payroll.other_deductions || 0).toString(),
      bonuses: (payroll.bonuses || 0).toString(),
      bonus: (payroll.bonus || 0).toString(),
      payment_date: payroll.payment_date || '',
      payment_status: payroll.payment_status,
      notes: payroll.notes || ''
    });
    const staff = staffList.find(s => s.user.id === payroll.user_id);
    setSelectedStaff(staff);
    // Fetch other period's off-days for cross-period calculation
    fetchOtherPeriodOffDays(payroll.user_id, payroll.month, payroll.year, payroll.period || 1);
    setPayrollOpen(true);
  };

  const handleEditProfile = (staff) => {
    setProfileForm({
      user_id: staff.user.id,
      employee_name: staff.user.name || '',
      employee_id: staff.profile?.employee_id || '',
      date_of_hire: staff.profile?.date_of_hire || '',
      bank_name: staff.profile?.bank_name || '',
      bank_account_name: staff.profile?.bank_account_name || '',
      bank_account_number: staff.profile?.bank_account_number || '',
      base_salary: staff.profile?.base_salary?.toString() || ''
    });
    setProfileOpen(true);
  };

  const handleDeletePayroll = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payroll record?')) return;
    try {
      await axios.delete(`${API}/payroll/${id}`, getAuthHeader());
      toast.success('Payroll deleted successfully');
      fetchPayrolls();
    } catch (error) {
      toast.error('Failed to delete payroll');
    }
  };

  const handlePayrollClose = () => {
    setPayrollOpen(false);
    setEditingPayroll(null);
    setSelectedStaff(null);
    setOtherPeriodOffDaysSpent(0);
    setPayrollForm({
      user_id: '',
      month: currentMonth,
      period: 1,
      year: currentYear,
      days_worked: '',
      entitled_day_offs: '0',
      off_days_spent: '0',
      absent_days: '0',
      // Additions
      data_allowance: '0',
      medical_allowance: '0',
      offsite_duty_allowance: '0',
      // Multiple bonus fields
      bonus_1_description: '',
      bonus_1_amount: '0',
      bonus_2_description: '',
      bonus_2_amount: '0',
      bonus_3_description: '',
      bonus_3_amount: '0',
      // Deductions
      loan_repayment: '0',
      damages: '0',
      other_deduction_amount: '0',
      other_deduction_description: '',
      loan_amount: '0',
      loan_disbursed_date: '',
      // Year-to-date totals
      ytd_gross_pay: '',
      ytd_net_pay: '',
      // Legacy fields
      other_deductions: '0',
      bonuses: '0',
      bonus: '0',
      payment_date: '',
      payment_status: 'pending',
      notes: ''
    });
  };

  const handleProfileClose = () => {
    setProfileOpen(false);
    setProfileForm({
      user_id: '',
      employee_name: '',
      employee_id: '',
      date_of_hire: '',
      bank_name: '',
      bank_account_name: '',
      bank_account_number: '',
      base_salary: ''
    });
  };

  const handleDeleteProfile = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete the employee profile for ${userName}? This will remove their payroll profile but NOT their user account.`)) return;
    try {
      await axios.delete(`${API}/employee-profiles/${userId}`, getAuthHeader());
      toast.success('Employee profile deleted successfully');
      fetchStaffList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete profile');
    }
  };

  const handleAddNewProfile = () => {
    setProfileForm({
      user_id: '',
      employee_name: '',
      employee_id: '',
      date_of_hire: '',
      bank_name: '',
      bank_account_name: '',
      bank_account_number: '',
      base_salary: ''
    });
    setProfileOpen(true);
  };

  const handleViewPayslip = async (payroll) => {
    // Fetch the individual payroll record to get dynamically calculated YTD values
    try {
      const payrollResponse = await axios.get(`${API}/payroll/${payroll.id}`, getAuthHeader());
      setViewingPayslip(payrollResponse.data);
    } catch (error) {
      // Fallback to the list data if fetch fails
      setViewingPayslip(payroll);
    }
    // Fetch employee profile for this staff member
    try {
      const response = await axios.get(`${API}/employee-profiles/${payroll.user_id}`, getAuthHeader());
      setViewingEmployeeProfile(response.data);
    } catch (error) {
      setViewingEmployeeProfile(null);
    }
    setViewPayslipOpen(true);
  };

  const handleClosePayslipView = () => {
    setViewPayslipOpen(false);
    setViewingPayslip(null);
    setViewingEmployeeProfile(null);
  };

  const getMonthName = (month) => MONTHS.find(m => m.value === month)?.label || '';

  const totalNetPay = payrolls.reduce((sum, p) => sum + (p.net_pay || 0), 0);
  const totalPaid = payrolls.filter(p => p.payment_status === 'paid').reduce((sum, p) => sum + (p.net_pay || 0), 0);
  const totalPending = payrolls.filter(p => p.payment_status === 'pending').reduce((sum, p) => sum + (p.net_pay || 0), 0);
  
  // Period-specific stats
  const period1Total = payrolls.filter(p => (p.period || 1) === 1).reduce((sum, p) => sum + (p.net_pay || 0), 0);
  const period2Total = payrolls.filter(p => p.period === 2).reduce((sum, p) => sum + (p.net_pay || 0), 0);
  const getPeriodLabel = () => {
    if (filterPeriod === '1') return 'Period 1 (Mid-Month)';
    if (filterPeriod === '2') return 'Period 2 (Month-End)';
    return 'All Periods';
  };

  const exportFileBase = () => `payroll_${getMonthName(filterMonth)}_${filterYear}${filterPeriod !== 'all' ? `_P${filterPeriod}` : ''}`;

  const payrollExportRows = () => payrolls.map((p) => {
    const additions = (p.total_additions || 0) > 0 ? p.total_additions : (p.bonuses || 0);
    const deductions = (p.total_deductions || 0) > 0 ? p.total_deductions : ((p.absence_deduction || 0) + (p.other_deductions || 0));
    return {
      employee: p.staff_name || '',
      empId: p.employee_id || '',
      period: `P${p.period || 1}`,
      base: p.base_salary || 0,
      days: `${p.days_worked ?? 0}/${p.days_in_month ?? 0}`,
      absent: p.absent_days ?? 0,
      gross: p.gross_pay || 0,
      additions,
      deductions,
      net: p.net_pay || 0,
      status: p.payment_status || '',
    };
  });

  const exportPayrollCSV = () => {
    if (payrolls.length === 0) { toast.error('No payroll records to export'); return; }
    const headers = ['Employee', 'Emp. ID', 'Period', 'Base Salary', 'Days Worked', 'Absent', 'Gross Pay', 'Additions', 'Deductions', 'Net Pay', 'Status'];
    const esc = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = payrollExportRows().map((r) => [r.employee, r.empId, r.period, r.base, r.days, r.absent, r.gross, r.additions, r.deductions, r.net, r.status]);
    rows.push([]);
    rows.push(['Totals', '', '', '', '', '', '', '', '', totalNetPay, `Paid ${currencySymbol}${formatCurrency(totalPaid)} / Pending ${currencySymbol}${formatCurrency(totalPending)}`]);
    const csv = [headers, ...rows].map((row) => row.map(esc).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${exportFileBase()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success(`Exported ${payrolls.length} record(s) to CSV`);
  };

  const exportPayrollPDF = () => {
    if (payrolls.length === 0) { toast.error('No payroll records to export'); return; }
    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const marginX = 30;
    let y = 44;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(15, 23, 42);
    doc.text('Payroll Report', marginX, y);
    y += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`${getMonthName(filterMonth)} ${filterYear}  ·  ${getPeriodLabel()}  ·  ${payrolls.length} record(s)  ·  Exported ${new Date().toLocaleString()}`, marginX, y);
    y += 14;

    const cols = [
      { h: 'Employee', w: 120, a: 'left' },
      { h: 'Emp. ID', w: 65, a: 'left' },
      { h: 'Period', w: 42, a: 'center' },
      { h: 'Base', w: 72, a: 'right' },
      { h: 'Days', w: 48, a: 'center' },
      { h: 'Absent', w: 44, a: 'center' },
      { h: 'Gross', w: 72, a: 'right' },
      { h: 'Additions', w: 72, a: 'right' },
      { h: 'Deductions', w: 74, a: 'right' },
      { h: 'Net Pay', w: 78, a: 'right' },
      { h: 'Status', w: 55, a: 'left' },
    ];
    const money = (v) => `${currencySymbol}${formatCurrency(v || 0)}`;
    const rowH = 18;

    const drawHeader = () => {
      doc.setFillColor(16, 185, 129);
      doc.rect(marginX, y, cols.reduce((s, c) => s + c.w, 0), rowH, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(255, 255, 255);
      let x = marginX;
      for (const c of cols) {
        const tx = c.a === 'right' ? x + c.w - 4 : c.a === 'center' ? x + c.w / 2 : x + 4;
        doc.text(c.h, tx, y + 12, { align: c.a });
        x += c.w;
      }
      y += rowH;
    };
    drawHeader();

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const rows = payrollExportRows();
    rows.forEach((r, i) => {
      if (y + rowH > pageH - 36) { doc.addPage(); y = 44; drawHeader(); doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); }
      if (i % 2 === 0) { doc.setFillColor(241, 245, 249); doc.rect(marginX, y, cols.reduce((s, c) => s + c.w, 0), rowH, 'F'); }
      const vals = [r.employee, r.empId, r.period, money(r.base), r.days, String(r.absent), money(r.gross), `+${money(r.additions)}`, `-${money(r.deductions)}`, money(r.net), r.status];
      let x = marginX;
      doc.setTextColor(30, 41, 59);
      vals.forEach((v, ci) => {
        const c = cols[ci];
        const tx = c.a === 'right' ? x + c.w - 4 : c.a === 'center' ? x + c.w / 2 : x + 4;
        doc.text(doc.splitTextToSize(String(v), c.w - 6)[0] || '', tx, y + 12, { align: c.a });
        x += c.w;
      });
      y += rowH;
    });

    // Totals
    if (y + 46 > pageH - 36) { doc.addPage(); y = 44; }
    y += 10;
    doc.setDrawColor(203, 213, 225);
    doc.line(marginX, y, marginX + cols.reduce((s, c) => s + c.w, 0), y);
    y += 16;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(15, 23, 42);
    doc.text(`Total Net Pay: ${money(totalNetPay)}`, marginX, y);
    doc.text(`Paid: ${money(totalPaid)}`, marginX + 220, y);
    doc.text(`Pending: ${money(totalPending)}`, marginX + 380, y);

    doc.save(`${exportFileBase()}.pdf`);
    toast.success(`Exported ${payrolls.length} record(s) to PDF`);
  };

  return (
    <div className="space-y-4 md:space-y-6" data-testid="payroll-management-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Payroll Management
          </h1>
          <p className="text-sm md:text-lg text-slate-600 mt-1 md:mt-2">Manage staff salaries and payments</p>
        </div>
        {userRole === 'admin' && (
          <Button variant="outline" onClick={() => { setRelinkOpen(true); setRelinkMatches(null); }} data-testid="relink-payslips-btn">
            <Users className="w-4 h-4 mr-2" />
            Re-link Payslips
          </Button>
        )}
      </div>

      {/* Re-link Payslips dialog (fix payslips pointing to an old/deleted account) */}
      <Dialog open={relinkOpen} onOpenChange={setRelinkOpen}>
        <DialogContent className="max-w-2xl" data-testid="relink-dialog">
          <DialogHeader>
            <DialogTitle>Re-link Payslips to an Employee</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Use this when an employee can't see their payslips because their login account was re-created.
              Search payslips by the name stored on them, then link them to the employee's current account.
            </p>
            <div>
              <Label className="text-sm">Staff name on the payslips</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={relinkName}
                  onChange={(e) => setRelinkName(e.target.value)}
                  placeholder="e.g. Okewale mufutau"
                  data-testid="relink-name-input"
                />
                <Button onClick={handleRelinkPreview} disabled={relinkBusy} variant="secondary" data-testid="relink-search-btn">
                  Search
                </Button>
              </div>
            </div>

            {relinkMatches !== null && (
              <div className="border rounded-lg p-3 bg-slate-50 max-h-56 overflow-y-auto" data-testid="relink-matches">
                {relinkMatches.length === 0 ? (
                  <p className="text-sm text-slate-500">No payslips match that name.</p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-slate-700 mb-2">{relinkMatches.length} payslip(s) found:</p>
                    <ul className="space-y-1 text-sm">
                      {relinkMatches.map((m) => (
                        <li key={m.id} className="flex justify-between gap-2 border-b border-slate-200 pb-1">
                          <span>{m.staff_name} · {m.month}/{m.year}{m.period ? ` (P${m.period})` : ''}</span>
                          <span className={m.linked_user_exists ? 'text-slate-500' : 'text-red-600 font-medium'}>
                            {m.linked_user_exists ? `linked: ${m.linked_user_name}` : 'linked to missing account'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}

            <div>
              <Label className="text-sm">Link these payslips to employee</Label>
              <Popover open={relinkTargetOpen} onOpenChange={setRelinkTargetOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between mt-1 font-normal"
                    data-testid="relink-target-select"
                  >
                    {relinkTargetId
                      ? (() => { const s = staffList.find((x) => x.user.id === relinkTargetId); return s ? `${s.user.name} (${s.user.role})` : 'Select the correct employee'; })()
                      : 'Select the correct employee'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search employee by name..." className="focus:ring-0 focus-visible:ring-0 outline-none" data-testid="relink-target-search" />
                    <CommandList onWheel={(e) => e.stopPropagation()}>
                      <CommandEmpty>No employee found.</CommandEmpty>
                      <CommandGroup>
                        {staffList.map((s) => (
                          <CommandItem
                            key={s.user.id}
                            value={`${s.user.name} ${s.user.role} ${s.user.username || ''} ${s.user.id}`}
                            onSelect={() => { setRelinkTargetId(s.user.id); setRelinkTargetOpen(false); }}
                            data-testid={`relink-target-option-${s.user.id}`}
                          >
                            <Check className={`mr-2 h-4 w-4 ${relinkTargetId === s.user.id ? 'opacity-100' : 'opacity-0'}`} />
                            {s.user.name} <span className="ml-1 text-xs text-slate-400">({s.user.role})</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRelinkOpen(false)} data-testid="relink-cancel-btn">Cancel</Button>
              <Button
                onClick={handleRelinkCommit}
                disabled={relinkBusy || !relinkTargetId || !relinkMatches || relinkMatches.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="relink-confirm-btn"
              >
                Re-link {relinkMatches && relinkMatches.length > 0 ? `${relinkMatches.length} ` : ''}Payslip(s)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="payroll" className="space-y-4 md:space-y-6">
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="payroll" data-testid="payroll-tab" className="flex-1 sm:flex-none">
            <DollarSign className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Payroll</span>
            <span className="sm:hidden">Pay</span>
          </TabsTrigger>
          <TabsTrigger value="employees" data-testid="employees-tab" className="flex-1 sm:flex-none">
            <Users className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Employee Profiles</span>
            <span className="sm:hidden">Profiles</span>
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="history-tab" className="flex-1 sm:flex-none">
            <Calendar className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Payroll History</span>
            <span className="sm:hidden">History</span>
          </TabsTrigger>
        </TabsList>

        {/* Payroll Tab */}
        <TabsContent value="payroll" className="space-y-4 md:space-y-6">
          {/* Filter and Add */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-end sm:justify-between">
            <div className="flex gap-2 sm:gap-4 flex-wrap">
              <div className="flex-1 sm:flex-none">
                <Label className="text-xs sm:text-sm">Month</Label>
                <Select value={filterMonth.toString()} onValueChange={(v) => setFilterMonth(parseInt(v))}>
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(m => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 sm:flex-none">
                <Label className="text-xs sm:text-sm">Period</Label>
                <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                  <SelectTrigger className="w-full sm:w-36" data-testid="filter-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Periods</SelectItem>
                    <SelectItem value="1">Period 1 (Mid-Month)</SelectItem>
                    <SelectItem value="2">Period 2 (Month-End)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 sm:flex-none">
                <Label className="text-xs sm:text-sm">Year</Label>
                <Select value={filterYear.toString()} onValueChange={(v) => setFilterYear(parseInt(v))}>
                  <SelectTrigger className="w-full sm:w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex gap-2 flex-wrap items-end">
              <Button type="button" variant="outline" onClick={exportPayrollCSV} data-testid="export-payroll-csv-button" className="border-slate-300">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                CSV
              </Button>
              <Button type="button" variant="outline" onClick={exportPayrollPDF} data-testid="export-payroll-pdf-button" className="border-slate-300">
                <FileDown className="w-4 h-4 mr-2" />
                PDF
              </Button>
            <Dialog open={payrollOpen} onOpenChange={setPayrollOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-payroll-button" className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Payroll
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="payroll-dialog">
                <DialogHeader>
                  <DialogTitle>{editingPayroll ? 'Edit Payroll' : 'Create Payroll'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handlePayrollSubmit} className="space-y-4">
                  {!editingPayroll && (
                    <div>
                      <Label>Select Staff *</Label>
                      <Select onValueChange={handleStaffSelect} value={payrollForm.user_id}>
                        <SelectTrigger data-testid="staff-select">
                          <SelectValue placeholder="Select a staff member" />
                        </SelectTrigger>
                        <SelectContent>
                          {staffList.filter(s => s.profile).map(s => (
                            <SelectItem key={s.user.id} value={s.user.id}>
                              {s.user.name} ({s.profile?.employee_id || 'No ID'})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {staffList.filter(s => !s.profile).length > 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          {staffList.filter(s => !s.profile).length} staff without profiles. Create employee profiles first.
                        </p>
                      )}
                    </div>
                  )}
                  
                  {(selectedStaff || editingPayroll) && (
                    <>
                      {selectedStaff?.profile && (
                        <div className="p-3 bg-slate-50 rounded-lg text-sm">
                          <p><strong>Base Salary:</strong> {currencySymbol}{formatCurrency(selectedStaff.profile.base_salary)}/month</p>
                          <p><strong>Employee ID:</strong> {selectedStaff.profile.employee_id}</p>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Month *</Label>
                          <Select value={payrollForm.month.toString()} onValueChange={(v) => {
                            const newMonth = parseInt(v);
                            setPayrollForm({...payrollForm, month: newMonth});
                            fetchOtherPeriodOffDays(payrollForm.user_id, newMonth, payrollForm.year, payrollForm.period);
                          }}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {MONTHS.map(m => (
                                <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Period *</Label>
                          <Select value={payrollForm.period.toString()} onValueChange={(v) => {
                            const newPeriod = parseInt(v);
                            setPayrollForm({...payrollForm, period: newPeriod});
                            fetchOtherPeriodOffDays(payrollForm.user_id, payrollForm.month, payrollForm.year, newPeriod);
                          }}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">Period 1</SelectItem>
                              <SelectItem value="2">Period 2</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Year *</Label>
                          <Select value={payrollForm.year.toString()} onValueChange={(v) => {
                            const newYear = parseInt(v);
                            setPayrollForm({...payrollForm, year: newYear});
                            fetchOtherPeriodOffDays(payrollForm.user_id, payrollForm.month, newYear, payrollForm.period);
                          }}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Days Worked *</Label>
                          <Input
                            type="number"
                            data-testid="days-worked-input"
                            value={payrollForm.days_worked}
                            onChange={(e) => setPayrollForm({...payrollForm, days_worked: e.target.value})}
                            min="0"
                            max="31"
                            required
                          />
                        </div>
                        <div>
                          <Label>Entitled Off-Days</Label>
                          <Input
                            type="number"
                            value={payrollForm.entitled_day_offs}
                            onChange={(e) => setPayrollForm({...payrollForm, entitled_day_offs: e.target.value})}
                            min="0"
                          />
                        </div>
                        <div>
                          <Label>Off-Days Spent this Pay Period</Label>
                          <Input
                            type="number"
                            value={payrollForm.off_days_spent}
                            onChange={(e) => setPayrollForm({...payrollForm, off_days_spent: e.target.value})}
                            min="0"
                          />
                        </div>
                        <div>
                          <Label>Available Off-Days</Label>
                          <Input
                            type="number"
                            value={Math.max(0, (parseInt(payrollForm.entitled_day_offs) || 0) - (parseInt(payrollForm.off_days_spent) || 0) - otherPeriodOffDaysSpent)}
                            disabled
                            className="bg-slate-100"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Entitled - Period 1 Spent - Period 2 Spent
                          </p>
                        </div>
                        <div>
                          <Label>Off-Days to Cash</Label>
                          <Input
                            type="number"
                            value={parseInt(payrollForm.period) === 2 ? Math.max(0, (parseInt(payrollForm.entitled_day_offs) || 0) - (parseInt(payrollForm.off_days_spent) || 0) - otherPeriodOffDaysSpent) : 0}
                            disabled
                            className="bg-blue-100 font-medium"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            {parseInt(payrollForm.period) === 2 ? 'Available Off-Days (Period 2 only)' : 'Only applicable in Period 2'}
                          </p>
                        </div>
                        <div>
                          <Label>Absent Days</Label>
                          <Input
                            type="number"
                            value={payrollForm.absent_days}
                            onChange={(e) => setPayrollForm({...payrollForm, absent_days: e.target.value})}
                            min="0"
                          />
                        </div>
                      </div>
                      
                      {/* Additions Section */}
                      <div className="border rounded-lg p-4 bg-emerald-50/50">
                        <h4 className="font-semibold text-emerald-700 mb-3">Additions</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Data Allowance ({currencySymbol})</Label>
                            <Input
                              type="number"
                              data-testid="data-allowance-input"
                              value={payrollForm.data_allowance}
                              onChange={(e) => setPayrollForm({...payrollForm, data_allowance: e.target.value})}
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <div>
                            <Label>Medical Allowance ({currencySymbol})</Label>
                            <Input
                              type="number"
                              data-testid="medical-allowance-input"
                              value={payrollForm.medical_allowance}
                              onChange={(e) => setPayrollForm({...payrollForm, medical_allowance: e.target.value})}
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <div>
                            <Label>Off-Site Duty Allowance ({currencySymbol})</Label>
                            <Input
                              type="number"
                              data-testid="offsite-duty-allowance-input"
                              value={payrollForm.offsite_duty_allowance}
                              onChange={(e) => setPayrollForm({...payrollForm, offsite_duty_allowance: e.target.value})}
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <div>
                            <Label>Off-Days Pay ({currencySymbol})</Label>
                            <Input
                              type="number"
                              value={(() => {
                                const entitledDayOffs = parseInt(payrollForm.entitled_day_offs) || 0;
                                const offDaysSpent = parseInt(payrollForm.off_days_spent) || 0;
                                const availableOffDays = Math.max(0, entitledDayOffs - offDaysSpent - otherPeriodOffDaysSpent);
                                const dailyRate = selectedStaff?.profile?.base_salary 
                                  ? (selectedStaff.profile.base_salary / 30) 
                                  : 0;
                                return (availableOffDays * dailyRate * 1.5).toFixed(2);
                              })()}
                              disabled
                              className="bg-emerald-100 font-medium"
                            />
                            <p className="text-xs text-slate-500 mt-1">Available Off-Days × Daily Rate × 1.5</p>
                          </div>
                          <div>
                            <Label>Days to Cash ({currencySymbol})</Label>
                            <Input
                              type="number"
                              value={(() => {
                                if (parseInt(payrollForm.period) !== 2) return '0.00';
                                const entitledDayOffs = parseInt(payrollForm.entitled_day_offs) || 0;
                                const offDaysSpent = parseInt(payrollForm.off_days_spent) || 0;
                                const offDaysToCash = Math.max(0, entitledDayOffs - offDaysSpent);
                                const dailyRate = selectedStaff?.profile?.base_salary 
                                  ? (selectedStaff.profile.base_salary / 30) 
                                  : 0;
                                return (offDaysToCash * dailyRate).toFixed(2);
                              })()}
                              disabled
                              className="bg-blue-100 font-medium"
                            />
                            <p className="text-xs text-slate-500 mt-1">Off-Days to Cash × Daily Rate (Period 2 only)</p>
                          </div>
                        </div>
                        
                        {/* Multiple Bonus Fields */}
                        <div className="mt-4 pt-3 border-t border-emerald-200">
                          <Label className="text-emerald-700 font-medium mb-2 block">Bonus</Label>
                          <div className="space-y-3">
                            {/* Bonus 1 */}
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                type="text"
                                data-testid="bonus-1-description-input"
                                value={payrollForm.bonus_1_description}
                                onChange={(e) => setPayrollForm({...payrollForm, bonus_1_description: e.target.value})}
                                placeholder="Bonus 1 description..."
                              />
                              <Input
                                type="number"
                                data-testid="bonus-1-amount-input"
                                value={payrollForm.bonus_1_amount}
                                onChange={(e) => setPayrollForm({...payrollForm, bonus_1_amount: e.target.value})}
                                min="0"
                                step="0.01"
                                placeholder={`Amount (${currencySymbol})`}
                              />
                            </div>
                            {/* Bonus 2 */}
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                type="text"
                                data-testid="bonus-2-description-input"
                                value={payrollForm.bonus_2_description}
                                onChange={(e) => setPayrollForm({...payrollForm, bonus_2_description: e.target.value})}
                                placeholder="Bonus 2 description..."
                              />
                              <Input
                                type="number"
                                data-testid="bonus-2-amount-input"
                                value={payrollForm.bonus_2_amount}
                                onChange={(e) => setPayrollForm({...payrollForm, bonus_2_amount: e.target.value})}
                                min="0"
                                step="0.01"
                                placeholder={`Amount (${currencySymbol})`}
                              />
                            </div>
                            {/* Bonus 3 */}
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                type="text"
                                data-testid="bonus-3-description-input"
                                value={payrollForm.bonus_3_description}
                                onChange={(e) => setPayrollForm({...payrollForm, bonus_3_description: e.target.value})}
                                placeholder="Bonus 3 description..."
                              />
                              <Input
                                type="number"
                                data-testid="bonus-3-amount-input"
                                value={payrollForm.bonus_3_amount}
                                onChange={(e) => setPayrollForm({...payrollForm, bonus_3_amount: e.target.value})}
                                min="0"
                                step="0.01"
                                placeholder={`Amount (${currencySymbol})`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Deductions Section */}
                      <div className="border rounded-lg p-4 bg-red-50/50">
                        <h4 className="font-semibold text-red-700 mb-3">Deductions</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Loan Repayment ({currencySymbol})</Label>
                            <Input
                              type="number"
                              data-testid="loan-repayment-input"
                              value={payrollForm.loan_repayment}
                              onChange={(e) => setPayrollForm({...payrollForm, loan_repayment: e.target.value})}
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <div>
                            <Label>Damages ({currencySymbol})</Label>
                            <Input
                              type="number"
                              data-testid="damages-input"
                              value={payrollForm.damages}
                              onChange={(e) => setPayrollForm({...payrollForm, damages: e.target.value})}
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <div>
                            <Label>Other Deduction ({currencySymbol})</Label>
                            <Input
                              type="number"
                              data-testid="other-deduction-amount-input"
                              value={payrollForm.other_deduction_amount}
                              onChange={(e) => setPayrollForm({...payrollForm, other_deduction_amount: e.target.value})}
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <div>
                            <Label>Other Deduction Description</Label>
                            <Input
                              type="text"
                              data-testid="other-deduction-description-input"
                              value={payrollForm.other_deduction_description}
                              onChange={(e) => setPayrollForm({...payrollForm, other_deduction_description: e.target.value})}
                              placeholder="Specify reason..."
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Staff Loan Section */}
                      <div className="border rounded-lg p-4 bg-amber-50/50">
                        <h4 className="font-semibold text-amber-700 mb-1">Staff Loan</h4>
                        <p className="text-xs text-slate-500 mb-3">
                          Enter the Loan Amount &amp; Date Disbursed on the period the loan is given (leave at 0 on later periods — it carries forward). Enter the per-period <span className="font-medium">Loan Repayment</span> in the Deductions section above. Totals below are auto-calculated.
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Loan Amount / Principal ({currencySymbol})</Label>
                            <Input
                              type="number"
                              data-testid="loan-amount-input"
                              value={payrollForm.loan_amount}
                              onChange={(e) => setPayrollForm({...payrollForm, loan_amount: e.target.value})}
                              min="0"
                              step="0.01"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <Label>Date Disbursed</Label>
                            <Input
                              type="date"
                              data-testid="loan-disbursed-date-input"
                              value={payrollForm.loan_disbursed_date}
                              onChange={(e) => setPayrollForm({...payrollForm, loan_disbursed_date: e.target.value})}
                            />
                          </div>
                        </div>
                        {editingPayroll && (editingPayroll.loan_active_amount > 0) && (
                          <div className="mt-4 pt-3 border-t border-amber-200" data-testid="loan-computed-summary">
                            {(editingPayroll.loan_balance || 0) === 0 && (
                              <div className="mb-2">
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 inline-flex items-center gap-1" data-testid="form-loan-cleared-badge">
                                  <CheckCircle className="w-3 h-3" /> Loan cleared
                                </span>
                              </div>
                            )}
                            <div className="grid grid-cols-3 gap-3">
                            <div className="text-center bg-white/70 rounded-md py-2">
                              <p className="text-[11px] text-slate-500">Total Repaid to Date</p>
                              <p className="text-sm font-bold text-emerald-700">{currencySymbol}{(editingPayroll.loan_total_repaid || 0).toLocaleString()}</p>
                              <p className="text-[10px] text-slate-400">{editingPayroll.loan_payments_count || 0} payment{(editingPayroll.loan_payments_count || 0) === 1 ? '' : 's'} so far</p>
                            </div>
                            <div className="text-center bg-white/70 rounded-md py-2">
                              <p className="text-[11px] text-slate-500">Loan Amount</p>
                              <p className="text-sm font-bold text-slate-700">{currencySymbol}{(editingPayroll.loan_active_amount || 0).toLocaleString()}</p>
                            </div>
                            <div className="text-center bg-white/70 rounded-md py-2">
                              <p className="text-[11px] text-slate-500">Balance Remaining</p>
                              <p className="text-sm font-bold text-red-700">{currencySymbol}{(editingPayroll.loan_balance || 0).toLocaleString()}</p>
                            </div>
                            </div>
                          </div>
                        )}
                        {!editingPayroll && (
                          <p className="text-xs text-amber-600 mt-2">Save the payroll to see the auto-calculated repayment totals.</p>
                        )}
                      </div>

                      {/* Medical Reimbursement Section (Record Only - Not part of Earnings/Deductions) */}
                      <div className="border rounded-lg p-4 bg-purple-50/50">
                        <h4 className="font-semibold text-purple-700 mb-1">Medical Reimbursement</h4>
                        <p className="text-xs text-slate-500 mb-3">For record purposes only - not included in earnings or deductions.</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Amount Paid ({currencySymbol})</Label>
                            <Input
                              type="number"
                              data-testid="med-reimburse-amount-input"
                              value={payrollForm.med_reimburse_amount}
                              onChange={(e) => setPayrollForm({...payrollForm, med_reimburse_amount: e.target.value})}
                              min="0"
                              step="0.01"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <Label>Date Paid</Label>
                            <Input
                              type="date"
                              data-testid="med-reimburse-date-input"
                              value={payrollForm.med_reimburse_date}
                              onChange={(e) => setPayrollForm({...payrollForm, med_reimburse_date: e.target.value})}
                            />
                          </div>
                        </div>
                        <div className="mt-3">
                          <Label>Reason for Reimbursement</Label>
                          <Input
                            type="text"
                            data-testid="med-reimburse-reason-input"
                            value={payrollForm.med_reimburse_reason}
                            onChange={(e) => setPayrollForm({...payrollForm, med_reimburse_reason: e.target.value})}
                            placeholder="e.g., Hospital visit, medication, etc."
                          />
                        </div>
                      </div>
                      
                      {/* Year-to-Date Section - Admin Only */}
                      {userRole === 'admin' && (
                        <div className="border rounded-lg p-4 bg-blue-50/50">
                          <h4 className="font-semibold text-blue-700 mb-3">Year-to-Date Totals (Admin Override)</h4>
                          <p className="text-xs text-slate-500 mb-3">These values are auto-calculated but can be overridden if needed.</p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>YTD Gross Pay ({currencySymbol})</Label>
                              <Input
                                type="number"
                                data-testid="ytd-gross-pay-input"
                                value={payrollForm.ytd_gross_pay}
                                onChange={(e) => setPayrollForm({...payrollForm, ytd_gross_pay: e.target.value})}
                                min="0"
                                step="0.01"
                                placeholder="Auto-calculated if empty"
                              />
                            </div>
                            <div>
                              <Label>YTD Net Pay ({currencySymbol})</Label>
                              <Input
                                type="number"
                                data-testid="ytd-net-pay-input"
                                value={payrollForm.ytd_net_pay}
                                onChange={(e) => setPayrollForm({...payrollForm, ytd_net_pay: e.target.value})}
                                min="0"
                                step="0.01"
                                placeholder="Auto-calculated if empty"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Payment Date</Label>
                          <Input
                            type="date"
                            value={payrollForm.payment_date}
                            onChange={(e) => setPayrollForm({...payrollForm, payment_date: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Payment Status</Label>
                          <Select value={payrollForm.payment_status} onValueChange={(v) => setPayrollForm({...payrollForm, payment_status: v})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="paid">Paid</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div>
                        <Label>Notes</Label>
                        <Textarea
                          value={payrollForm.notes}
                          onChange={(e) => setPayrollForm({...payrollForm, notes: e.target.value})}
                          rows={2}
                        />
                      </div>
                    </>
                  )}
                  
                  <div className="flex gap-2">
                    <Button type="submit" data-testid="payroll-submit-button" className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={!payrollForm.user_id && !editingPayroll}>
                      {editingPayroll ? 'Update' : 'Create Payroll'}
                    </Button>
                    <Button type="button" variant="outline" onClick={handlePayrollClose}>Cancel</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
            <div className="card p-3 md:p-4 bg-gradient-to-br from-blue-50 to-white border-l-4 border-blue-500">
              <p className="text-xs font-medium text-slate-500 uppercase">
                {getMonthName(filterMonth)} - {getPeriodLabel()}
              </p>
              <p className="text-xl md:text-2xl font-bold text-blue-600">{currencySymbol}{formatCurrency(totalNetPay)}</p>
              <p className="text-xs text-slate-500 mt-1">{payrolls.length} record(s)</p>
            </div>
            <div className="card p-3 md:p-4 bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-500">
              <p className="text-xs font-medium text-slate-500 uppercase">Paid</p>
              <p className="text-xl md:text-2xl font-bold text-emerald-600">{currencySymbol}{formatCurrency(totalPaid)}</p>
              <p className="text-xs text-slate-500 mt-1">{payrolls.filter(p => p.payment_status === 'paid').length} staff</p>
            </div>
            <div className="card p-3 md:p-4 bg-gradient-to-br from-amber-50 to-white border-l-4 border-amber-500">
              <p className="text-xs font-medium text-slate-500 uppercase">Pending</p>
              <p className="text-xl md:text-2xl font-bold text-amber-600">{currencySymbol}{formatCurrency(totalPending)}</p>
              <p className="text-xs text-slate-500 mt-1">{payrolls.filter(p => p.payment_status === 'pending').length} staff</p>
            </div>
            {filterPeriod === 'all' && (
              <div className="card p-3 md:p-4 bg-gradient-to-br from-purple-50 to-white border-l-4 border-purple-500">
                <p className="text-xs font-medium text-slate-500 uppercase">By Period</p>
                <div className="text-sm mt-1">
                  <p className="text-purple-700">P1: {currencySymbol}{formatCurrency(period1Total)}</p>
                  <p className="text-purple-700">P2: {currencySymbol}{formatCurrency(period2Total)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Recent Pay Slip Views Notification */}
          {recentViews.length > 0 && (
            <div className="card p-4 bg-gradient-to-r from-cyan-50 to-white border-l-4 border-cyan-500">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-5 h-5 text-cyan-600" />
                <h3 className="font-semibold text-cyan-800">Recent Pay Slip Views</h3>
                <span className="bg-cyan-100 text-cyan-700 text-xs px-2 py-0.5 rounded-full">{recentViews.length} in last 7 days</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {recentViews.slice(0, 10).map((view, idx) => (
                  <div key={idx} className="bg-white border border-cyan-200 rounded-lg px-3 py-2 text-sm shadow-sm">
                    <span className="font-medium text-slate-700">{view.staff_name}</span>
                    <span className="text-slate-400 mx-1">•</span>
                    <span className="text-cyan-600">{getMonthName(view.month)} P{view.period}</span>
                    <span className="text-slate-400 mx-1">•</span>
                    <span className="text-slate-500 text-xs">
                      {view.viewed_at ? new Date(view.viewed_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                ))}
              </div>
              {recentViews.length > 10 && (
                <p className="text-xs text-slate-500 mt-2">...and {recentViews.length - 10} more</p>
              )}
            </div>
          )}

          {/* Payroll Table */}
          <div className="card">
            <div className="overflow-x-auto -mx-px">
              <table className="min-w-[1100px]">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Emp. ID</th>
                    <th>Period</th>
                    <th className="text-right">Base Salary</th>
                    <th className="text-center">Days</th>
                    <th className="text-center">Absent</th>
                    <th className="text-right">Gross Pay</th>
                    <th className="text-right">Additions</th>
                    <th className="text-right">Deductions</th>
                    <th className="text-right">Net Pay</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="payroll-table-body">
                  {payrolls.map((payroll) => {
                    // Calculate totals for display (handle legacy data)
                    const totalAdditions = (payroll.total_additions || 0) > 0 
                      ? payroll.total_additions 
                      : (payroll.bonuses || 0);
                    const totalDeductions = (payroll.total_deductions || 0) > 0
                      ? payroll.total_deductions
                      : (payroll.absence_deduction || 0) + (payroll.other_deductions || 0);
                    
                    return (
                      <tr key={payroll.id}>
                        <td className="font-medium whitespace-nowrap">{payroll.staff_name}</td>
                        <td className="font-mono text-xs md:text-sm">{payroll.employee_id}</td>
                        <td className="whitespace-nowrap">
                          <span className="px-2 py-1 rounded bg-slate-100 text-slate-700 text-xs font-medium">
                            P{payroll.period || 1}
                          </span>
                        </td>
                        <td className="text-right whitespace-nowrap">{currencySymbol}{formatCurrency(payroll.base_salary)}</td>
                        <td className="text-center whitespace-nowrap">{payroll.days_worked}/{payroll.days_in_month}</td>
                        <td className="text-center text-red-600">{payroll.absent_days}</td>
                        <td className="text-right whitespace-nowrap">{currencySymbol}{formatCurrency(payroll.gross_pay)}</td>
                        <td className="text-right text-emerald-600 whitespace-nowrap">+{currencySymbol}{formatCurrency(totalAdditions)}</td>
                        <td className="text-right text-red-600 whitespace-nowrap">-{currencySymbol}{formatCurrency(totalDeductions)}</td>
                        <td className="text-right font-bold whitespace-nowrap">{currencySymbol}{formatCurrency(payroll.net_pay)}</td>
                        <td>
                          <div className="flex flex-col gap-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                              payroll.payment_status === 'paid' 
                                ? 'bg-emerald-100 text-emerald-700' 
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {payroll.payment_status}
                            </span>
                            {payroll.viewed_by_employee && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700 whitespace-nowrap flex items-center gap-1">
                                <Eye className="w-3 h-3" /> Viewed
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleViewPayslip(payroll)}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded"
                              title="View Pay Slip"
                              data-testid={`view-payslip-${payroll.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditPayroll(payroll)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeletePayroll(payroll.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {payrolls.length === 0 && (
                <div className="text-center py-8 md:py-12 text-slate-500 text-sm md:text-base">
                  No payroll records for {getMonthName(filterMonth)} {filterYear} {filterPeriod !== 'all' ? `(${getPeriodLabel()})` : ''}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Employee Profiles Tab */}
        <TabsContent value="employees" className="space-y-4 md:space-y-6">
          <div className="flex justify-end">
            <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-profile-button" className="bg-emerald-600 hover:bg-emerald-700 text-sm md:text-base">
                  <UserPlus className="w-4 h-4 mr-1 md:mr-2" />
                  <span className="hidden sm:inline">Add Employee Profile</span>
                  <span className="sm:hidden">Add Profile</span>
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="profile-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{profileForm.user_id && staffList.find(s => s.user.id === profileForm.user_id)?.profile ? 'Edit' : 'Create'} Employee Profile</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleProfileSubmit} className="space-y-4">
                  <div>
                    <Label>Select Staff *</Label>
                    <Select onValueChange={(v) => {
                      const staff = staffList.find(s => s.user.id === v);
                      setProfileForm({...profileForm, user_id: v, employee_name: staff?.user?.name || ''});
                    }} value={profileForm.user_id}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a staff member" />
                      </SelectTrigger>
                      <SelectContent>
                        {staffList.map(s => (
                          <SelectItem key={s.user.id} value={s.user.id}>
                            {s.user.name} ({s.user.role}) {s.profile ? '✓' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Employee Name *</Label>
                    <Input
                      data-testid="employee-name-input"
                      value={profileForm.employee_name}
                      onChange={(e) => setProfileForm({...profileForm, employee_name: e.target.value})}
                      placeholder="Full name"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Employee ID *</Label>
                      <Input
                        data-testid="employee-id-input"
                        value={profileForm.employee_id}
                        onChange={(e) => setProfileForm({...profileForm, employee_id: e.target.value})}
                        placeholder="e.g., EMP001"
                        required
                      />
                    </div>
                    <div>
                      <Label>Date of Hire *</Label>
                      <Input
                        type="date"
                        data-testid="hire-date-input"
                        value={profileForm.date_of_hire}
                        onChange={(e) => setProfileForm({...profileForm, date_of_hire: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Base Monthly Salary ({currencySymbol}) *</Label>
                    <Input
                      type="number"
                      data-testid="base-salary-input"
                      value={profileForm.base_salary}
                      onChange={(e) => setProfileForm({...profileForm, base_salary: e.target.value})}
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Bank Name</Label>
                      <Input
                        value={profileForm.bank_name}
                        onChange={(e) => setProfileForm({...profileForm, bank_name: e.target.value})}
                        placeholder="e.g., First Bank"
                      />
                    </div>
                    <div>
                      <Label>Account Owner's Name</Label>
                      <Input
                        value={profileForm.bank_account_name}
                        onChange={(e) => setProfileForm({...profileForm, bank_account_name: e.target.value})}
                        placeholder="Account holder name"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Bank Account Number</Label>
                      <Input
                        value={profileForm.bank_account_number}
                        onChange={(e) => setProfileForm({...profileForm, bank_account_number: e.target.value})}
                        placeholder="Account number"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button type="submit" data-testid="profile-submit-button" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                      Save Profile
                    </Button>
                    <Button type="button" variant="outline" onClick={handleProfileClose}>Cancel</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Employee Profiles Table */}
          <div className="card">
            <div className="overflow-x-auto -mx-px">
              <table className="min-w-[900px]">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Employee ID</th>
                    <th>Date of Hire</th>
                    <th className="text-right">Base Salary</th>
                    <th>Bank</th>
                    <th>Account No.</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="employees-table-body">
                  {staffList.map((staff) => (
                    <tr key={staff.user.id}>
                      <td className="font-medium whitespace-nowrap">{staff.user.name}</td>
                      <td>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          staff.user.role === 'staff' ? 'bg-emerald-100 text-emerald-700' :
                          staff.user.role === 'manager' ? 'bg-amber-100 text-amber-700' :
                          staff.user.role === 'support_staff' ? 'bg-teal-100 text-teal-700' :
                          staff.user.role === 'vet_doctor' ? 'bg-pink-100 text-pink-700' :
                          staff.user.role === 'distributor' ? 'bg-purple-100 text-purple-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {staff.user.role === 'support_staff' ? 'Support Staff' :
                           staff.user.role === 'vet_doctor' ? 'Vet Doctor' :
                           staff.user.role?.charAt(0).toUpperCase() + staff.user.role?.slice(1)}
                        </span>
                      </td>
                      <td className="font-mono text-xs md:text-sm">{staff.profile?.employee_id || '-'}</td>
                      <td className="whitespace-nowrap">{staff.profile?.date_of_hire || '-'}</td>
                      <td className="text-right whitespace-nowrap">
                        {staff.profile?.base_salary ? `${currencySymbol}${formatCurrency(staff.profile.base_salary)}` : '-'}
                      </td>
                      <td className="whitespace-nowrap">{staff.profile?.bank_name || '-'}</td>
                      <td className="font-mono text-xs md:text-sm">{staff.profile?.bank_account_number || '-'}</td>
                      <td>
                        {staff.profile ? (
                          <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                            Complete
                          </span>
                        ) : (
                          <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                            No Profile
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEditProfile(staff)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title={staff.profile ? 'Edit Profile' : 'Create Profile'}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          {staff.profile && (
                            <button
                              onClick={() => handleDeleteProfile(staff.user.id, staff.user.name)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Delete Profile"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {staffList.length === 0 && (
                <div className="text-center py-8 md:py-12 text-slate-500 text-sm md:text-base">
                  No staff members found
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Payroll History Tab */}
        <TabsContent value="history" className="space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Employee Selector */}
            <div className="lg:col-span-1">
              <div className="card p-4">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-600" />
                  Select Employee
                </h3>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {staffList.map((item) => {
                    const staff = item.user;
                    const profile = item.profile;
                    return (
                      <button
                        key={staff.id}
                        onClick={() => handleSelectHistoryEmployee(staff.id)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          selectedHistoryEmployee === staff.id
                            ? 'bg-emerald-100 border-2 border-emerald-500'
                            : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                        }`}
                        data-testid={`history-employee-${staff.id}`}
                      >
                        <div className="font-medium text-slate-900">{staff.name}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-2 mt-1">
                          <span className="capitalize">{staff.role?.replace('_', ' ')}</span>
                          {profile?.employee_id && (
                            <span className="text-emerald-600">• {profile.employee_id}</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Payroll History List */}
            <div className="lg:col-span-2">
              {!selectedHistoryEmployee ? (
                <div className="card p-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700">Select an Employee</h3>
                  <p className="text-slate-500 mt-2">
                    Choose an employee from the list to view their payroll history.
                  </p>
                </div>
              ) : loadingHistory ? (
                <div className="card p-12 text-center">
                  <div className="animate-pulse text-slate-500">Loading payroll history...</div>
                </div>
              ) : employeePayrollHistory.length === 0 ? (
                <div className="card p-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700">No Payroll Records</h3>
                  <p className="text-slate-500 mt-2">
                    No payroll records found for this employee.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary Card */}
                  {(() => {
                    const summary = getHistorySummary();
                    const selectedEmployee = staffList.find(s => s.user.id === selectedHistoryEmployee);
                    return summary && (
                      <div className="card p-4 bg-gradient-to-r from-emerald-50 to-white">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="font-bold text-lg text-slate-900">
                              {selectedEmployee?.user?.name || 'Employee'}
                            </h3>
                            <p className="text-sm text-slate-500">
                              {summary.payslipCount} payslip{summary.payslipCount !== 1 ? 's' : ''} on record
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Total Net Pay (All Time)</p>
                            <p className="text-2xl font-bold text-emerald-600">
                              {currencySymbol}{formatCurrency(summary.totalNet)}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 pt-3 border-t">
                          <div>
                            <p className="text-xs text-slate-500">Total Gross</p>
                            <p className="font-semibold text-slate-900">
                              {currencySymbol}{formatCurrency(summary.totalGross)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Total Deductions</p>
                            <p className="font-semibold text-red-600">
                              {currencySymbol}{formatCurrency(summary.totalDeductions)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Avg. Net/Period</p>
                            <p className="font-semibold text-emerald-600">
                              {currencySymbol}{formatCurrency(summary.avgNet)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* History List */}
                  <div className="card divide-y">
                    <div className="p-3 bg-slate-50 border-b">
                      <h4 className="font-semibold text-slate-800">Payment History</h4>
                    </div>
                    {employeePayrollHistory.map((payroll) => (
                      <div 
                        key={payroll.id} 
                        className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => {
                          setViewingPayslip(payroll);
                          setViewPayslipOpen(true);
                        }}
                        data-testid={`history-payroll-${payroll.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-slate-900">
                              {getMonthName(payroll.month)} {payroll.year}
                              {payroll.period && <span className="text-slate-500"> - Period {payroll.period}</span>}
                            </div>
                            <div className="text-sm text-slate-500 mt-1">
                              Base: {currencySymbol}{formatCurrency(payroll.base_salary || 0)}
                              {payroll.bonus > 0 && (
                                <span className="text-emerald-600 ml-2">
                                  +{currencySymbol}{formatCurrency(payroll.bonus)} bonus
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-emerald-600">
                              {currencySymbol}{formatCurrency(payroll.net_pay || 0)}
                            </div>
                            <div className={`text-xs px-2 py-0.5 rounded-full inline-block ${
                              payroll.payment_status === 'paid'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {payroll.payment_status === 'paid' ? 'Paid' : 'Pending'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* View Pay Slip Dialog */}
      <Dialog open={viewPayslipOpen} onOpenChange={handleClosePayslipView}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-payslip-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Pay Slip - Staff View
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrintPayslip}
                className="flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Print PDF
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          {viewingPayslip && (
            <div ref={payslipPrintRef} className="space-y-4">
              {/* Company Header */}
              <div className="company-header text-center pb-4 border-b-2 border-emerald-500">
                <img 
                  src={COMPANY_LOGO} 
                  alt={COMPANY_NAME}
                  className="company-logo w-20 h-20 rounded-full object-cover mx-auto mb-2"
                />
                <h2 className="company-name text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {COMPANY_NAME}
                </h2>
                <p className="company-tagline text-xs text-slate-500">Poultry Farm Management</p>
                <h3 className="payslip-title text-lg font-semibold mt-3">PAY SLIP</h3>
              </div>

              {/* Pay Period Header */}
              <div className="p-4 border rounded-lg bg-gradient-to-r from-emerald-50 to-white">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Pay Period
                    </h2>
                    <p className="text-slate-600 mt-1">
                      {getMonthName(viewingPayslip.month)} {viewingPayslip.year} - Period {viewingPayslip.period || 1}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      viewingPayslip.payment_status === 'paid'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {viewingPayslip.payment_status === 'paid' ? 'PAID' : 'PENDING'}
                    </span>
                    {viewingPayslip.payment_date && (
                      <p className="text-sm text-slate-500 mt-2">
                        Payment Date: {viewingPayslip.payment_date}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Employee Details */}
              <div className="employee-info p-4 border rounded-lg bg-slate-50">
                <h3 className="font-semibold text-emerald-700 mb-2">Employee Information</h3>
                <div className="info-grid grid grid-cols-2 gap-4 text-sm">
                  <div className="info-item">
                    <label className="text-slate-500">Employee Name</label>
                    <span className="font-semibold">{viewingPayslip.staff_name}</span>
                  </div>
                  <div className="info-item">
                    <label className="text-slate-500">Employee ID</label>
                    <span className="font-mono font-semibold">{viewingPayslip.employee_id}</span>
                  </div>
                </div>
              </div>

              {/* Bank Details (if available) */}
              {viewingEmployeeProfile?.bank_name && (
                <div className="p-4 border rounded-lg">
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Bank Details
                  </h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Bank</p>
                      <p className="font-medium">{viewingEmployeeProfile.bank_name}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Account Name</p>
                      <p className="font-medium">{viewingEmployeeProfile.bank_account_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Account Number</p>
                      <p className="font-mono font-medium">{viewingEmployeeProfile.bank_account_number}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Earnings */}
              <div className="p-4 border rounded-lg">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                  Earnings
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Base Monthly Salary</span>
                    <span className="font-medium">{currencySymbol}{formatCurrency(viewingPayslip.base_salary)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Daily Rate ({currencySymbol}{formatCurrency(viewingPayslip.base_salary)} ÷ {viewingPayslip.days_in_month} days)</span>
                    <span className="font-medium">{currencySymbol}{formatCurrency(viewingPayslip.daily_rate)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Days Worked</span>
                    <span className="font-medium">{viewingPayslip.days_worked} of {viewingPayslip.days_in_month} days</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Entitled Off-Days</span>
                    <span className="font-medium">{viewingPayslip.entitled_day_offs} days</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Off-Days Spent</span>
                    <span className="font-medium">{viewingPayslip.off_days_spent || 0} days</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Available Off-Days</span>
                    <span className="font-medium">{viewingPayslip.available_off_days ?? Math.max(0, (viewingPayslip.entitled_day_offs || 0) - (viewingPayslip.off_days_spent || 0))} days</span>
                  </div>
                  {viewingPayslip.period === 2 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Off-Days to Cash (Period 2)</span>
                      <span className="font-medium text-blue-600">{viewingPayslip.off_days_to_cash || 0} days</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm pt-2 border-t">
                    <span className="font-semibold text-slate-700">Gross Pay ({currencySymbol}{formatCurrency(viewingPayslip.daily_rate)} × {viewingPayslip.days_worked})</span>
                    <span className="font-bold text-emerald-600">{currencySymbol}{formatCurrency(viewingPayslip.gross_pay)}</span>
                  </div>
                </div>
              </div>

              {/* Additions */}
              <div className="p-4 border rounded-lg bg-emerald-50/30">
                <h3 className="font-semibold text-emerald-700 mb-4 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Additions
                </h3>
                <div className="space-y-3">
                  {(viewingPayslip.off_days_pay || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Off-Days Pay ({viewingPayslip.available_off_days || 0} × Daily Rate × 1.5)</span>
                      <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(viewingPayslip.off_days_pay)}</span>
                    </div>
                  )}
                  {(viewingPayslip.days_to_cash || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Days to Cash ({viewingPayslip.off_days_to_cash || 0} × Daily Rate)</span>
                      <span className="font-medium text-blue-600">+{currencySymbol}{formatCurrency(viewingPayslip.days_to_cash)}</span>
                    </div>
                  )}
                  {(viewingPayslip.data_allowance || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Data Allowance</span>
                      <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(viewingPayslip.data_allowance)}</span>
                    </div>
                  )}
                  {(viewingPayslip.medical_allowance || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Medical Allowance</span>
                      <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(viewingPayslip.medical_allowance)}</span>
                    </div>
                  )}
                  {(viewingPayslip.offsite_duty_allowance || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Off-Site Duty Allowance</span>
                      <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(viewingPayslip.offsite_duty_allowance)}</span>
                    </div>
                  )}
                  {/* Multiple Bonus Fields */}
                  {(viewingPayslip.bonus_1_amount || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Bonus: {viewingPayslip.bonus_1_description || 'Bonus 1'}</span>
                      <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(viewingPayslip.bonus_1_amount)}</span>
                    </div>
                  )}
                  {(viewingPayslip.bonus_2_amount || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Bonus: {viewingPayslip.bonus_2_description || 'Bonus 2'}</span>
                      <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(viewingPayslip.bonus_2_amount)}</span>
                    </div>
                  )}
                  {(viewingPayslip.bonus_3_amount || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Bonus: {viewingPayslip.bonus_3_description || 'Bonus 3'}</span>
                      <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(viewingPayslip.bonus_3_amount)}</span>
                    </div>
                  )}
                  {/* Legacy single bonus field for old records */}
                  {(viewingPayslip.bonus || 0) > 0 && !(viewingPayslip.bonus_1_amount || 0) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Bonus</span>
                      <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(viewingPayslip.bonus)}</span>
                    </div>
                  )}
                  {/* Legacy bonuses field for old records */}
                  {(viewingPayslip.bonuses || 0) > 0 && !(viewingPayslip.bonus || 0) && !(viewingPayslip.bonus_1_amount || 0) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Bonus</span>
                      <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(viewingPayslip.bonuses)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm pt-2 border-t">
                    <span className="font-semibold text-slate-700">Total Additions</span>
                    <span className="font-bold text-emerald-600">+{currencySymbol}{formatCurrency(
                      (viewingPayslip.total_additions || 0) > 0 
                        ? viewingPayslip.total_additions 
                        : (viewingPayslip.bonuses || 0)
                    )}</span>
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div className="p-4 border rounded-lg bg-red-50/30">
                <h3 className="font-semibold text-red-700 mb-4 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Deductions
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Absent Days</span>
                    <span className="font-medium text-red-600">{viewingPayslip.absent_days} days</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Absence Deduction ({currencySymbol}{formatCurrency(viewingPayslip.daily_rate)} × {viewingPayslip.absent_days})</span>
                    <span className="font-medium text-red-600">-{currencySymbol}{formatCurrency(viewingPayslip.absence_deduction)}</span>
                  </div>
                  {(viewingPayslip.loan_repayment || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Loan Repayment</span>
                      <span className="font-medium text-red-600">-{currencySymbol}{formatCurrency(viewingPayslip.loan_repayment)}</span>
                    </div>
                  )}
                  {(viewingPayslip.damages || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Damages</span>
                      <span className="font-medium text-red-600">-{currencySymbol}{formatCurrency(viewingPayslip.damages)}</span>
                    </div>
                  )}
                  {(viewingPayslip.other_deduction_amount || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Other: {viewingPayslip.other_deduction_description || 'Unspecified'}</span>
                      <span className="font-medium text-red-600">-{currencySymbol}{formatCurrency(viewingPayslip.other_deduction_amount)}</span>
                    </div>
                  )}
                  {/* Legacy other_deductions for old records */}
                  {(viewingPayslip.other_deductions || 0) > 0 && !(viewingPayslip.other_deduction_amount || 0) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Other Deductions</span>
                      <span className="font-medium text-red-600">-{currencySymbol}{formatCurrency(viewingPayslip.other_deductions)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm pt-2 border-t">
                    <span className="font-semibold text-slate-700">Total Deductions</span>
                    <span className="font-bold text-red-600">-{currencySymbol}{formatCurrency(
                      (viewingPayslip.total_deductions || 0) > 0
                        ? viewingPayslip.total_deductions
                        : (viewingPayslip.absence_deduction || 0) + (viewingPayslip.other_deductions || 0)
                    )}</span>
                  </div>
                </div>
              </div>

              {/* Net Pay */}
              <div className="p-4 border rounded-lg bg-gradient-to-r from-emerald-50 to-white">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-slate-600">Net Pay</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Gross Pay + Additions - Deductions
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {currencySymbol}{formatCurrency(viewingPayslip.net_pay)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Staff Loan Section (if there is an active loan) */}
              {(viewingPayslip.loan_active_amount || 0) > 0 && (
                <div className="p-4 border rounded-lg bg-gradient-to-r from-amber-50 to-white" data-testid="admin-payslip-loan-section">
                  <div className="flex items-center gap-2 mb-3">
                    <h4 className="font-semibold text-amber-700">Staff Loan</h4>
                    {(viewingPayslip.loan_balance || 0) === 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 inline-flex items-center gap-1" data-testid="admin-loan-cleared-badge">
                        <CheckCircle className="w-3 h-3" /> Loan cleared
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <p className="text-sm text-slate-600">Loan Amount</p>
                      <p className="text-lg font-semibold text-slate-800">{currencySymbol}{formatCurrency(viewingPayslip.loan_active_amount || 0)}</p>
                    </div>
                    {viewingPayslip.loan_active_date && (
                      <div>
                        <p className="text-sm text-slate-600">Date Disbursed</p>
                        <p className="text-lg font-semibold text-slate-800">{viewingPayslip.loan_active_date}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-slate-600">Repayment This Period</p>
                      <p className="text-lg font-semibold text-red-600">{currencySymbol}{formatCurrency(viewingPayslip.loan_repayment || 0)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Total Repaid to Date</p>
                      <p className="text-lg font-semibold text-emerald-600">{currencySymbol}{formatCurrency(viewingPayslip.loan_total_repaid || 0)}</p>
                      <p className="text-xs text-slate-500">{viewingPayslip.loan_payments_count || 0} payment{(viewingPayslip.loan_payments_count || 0) === 1 ? '' : 's'} made so far</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-amber-200">
                    <span className="font-semibold text-slate-700">Balance Remaining to be Repaid</span>
                    <span className="font-bold text-amber-700 text-lg">{currencySymbol}{formatCurrency(viewingPayslip.loan_balance || 0)}</span>
                  </div>
                </div>
              )}

              {/* Medical Reimbursement Section (if any) */}
              {(viewingPayslip.med_reimburse_amount > 0 || viewingPayslip.med_reimburse_mtd > 0 || viewingPayslip.med_reimburse_ytd > 0) && (
                <div className="p-4 border rounded-lg bg-gradient-to-r from-purple-50 to-white">
                  <h4 className="font-semibold text-purple-700 mb-3">Medical Reimbursement (For Record Only)</h4>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <p className="text-sm text-slate-600">Amount Paid This Period</p>
                      <p className="text-lg font-semibold text-purple-600">
                        {currencySymbol}{formatCurrency(viewingPayslip.med_reimburse_amount || 0)}
                      </p>
                    </div>
                    {viewingPayslip.med_reimburse_date && (
                      <div>
                        <p className="text-sm text-slate-600">Date Paid</p>
                        <p className="text-lg font-semibold text-purple-600">
                          {viewingPayslip.med_reimburse_date}
                        </p>
                      </div>
                    )}
                  </div>
                  {viewingPayslip.med_reimburse_reason && (
                    <div className="mb-3">
                      <p className="text-sm text-slate-600">Reason</p>
                      <p className="text-sm font-medium text-purple-700">{viewingPayslip.med_reimburse_reason}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-purple-200">
                    <div>
                      <p className="text-sm text-slate-600">Total This Month</p>
                      <p className="text-lg font-bold text-purple-600">
                        {currencySymbol}{formatCurrency(viewingPayslip.med_reimburse_mtd || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Total Year-to-Date</p>
                      <p className="text-lg font-bold text-purple-600">
                        {currencySymbol}{formatCurrency(viewingPayslip.med_reimburse_ytd || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Year-to-Date Totals */}
              <div className="p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-white">
                <h4 className="font-semibold text-blue-700 mb-3">Year-to-Date Totals ({viewingPayslip.year})</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-600">YTD Gross Pay</p>
                    <p className="text-xl font-bold text-blue-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {currencySymbol}{formatCurrency(viewingPayslip.ytd_gross_pay || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">YTD Net Pay</p>
                    <p className="text-xl font-bold text-blue-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {currencySymbol}{formatCurrency(viewingPayslip.ytd_net_pay || 0)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {viewingPayslip.notes && (
                <div className="p-4 border rounded-lg bg-slate-50">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">Notes:</span> {viewingPayslip.notes}
                  </p>
                </div>
              )}

              {/* Footer */}
              <div className="footer text-center text-xs text-slate-400 pt-4 mt-4 border-t">
                <p>Generated by {COMPANY_NAME} Payroll System</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrollManagement;
